// Borrowers.jsx — list, filter, create
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, Button, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, IconButton, Tooltip, CircularProgress,
} from "@mui/material";
import { Plus, Search, ArrowRight, Trash2, Filter } from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import { borrowersApi, formatApiError } from "../api/api";
import { fmtINR, fmtDateTime } from "../utils/format";

const INDUSTRIES = [
  "real_estate", "manufacturing", "power", "renewable", "logistics",
  "agriculture", "infrastructure", "telecom", "ports", "services",
];

const RATINGS = ["AAA", "AA+", "AA", "AA-", "A+", "A", "A-", "BBB+", "BBB", "BBB-", "BB+", "BB", "BB-", "B", "CCC", "Unrated"];

export default function Borrowers() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPortfolio, setFilterPortfolio] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);

  function defaultForm() {
    return {
      name: "", type: "corporate", portfolio: "lending", industry: "manufacturing",
      credit_rating: "BBB+", annual_turnover_inr: "", total_exposure_inr: "",
      outstanding_inr: "", facility_type: "", tenor_years: "", ltv_pct: 60,
      relationship_manager: "", registered_office_pin: "", registered_office_state: "",
    };
  }

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPortfolio) params.portfolio = filterPortfolio;
      if (filterIndustry) params.industry = filterIndustry;
      const { data } = await borrowersApi.list(params);
      setList(data.borrowers || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterPortfolio, filterIndustry]);

  const filtered = list.filter((b) =>
    !search || (b.name + " " + b.cif_no).toLowerCase().includes(search.toLowerCase())
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["annual_turnover_inr", "total_exposure_inr", "outstanding_inr", "tenor_years", "ltv_pct"]
        .forEach((k) => { payload[k] = payload[k] === "" ? null : Number(payload[k]); });
      await borrowersApi.create(payload);
      toast.success(`Borrower ${form.name} created`);
      setOpen(false); setForm(defaultForm()); load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete ${b.name} and all linked collaterals?`)) return;
    try {
      await borrowersApi.remove(b.borrower_id);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout title="Borrowers" subtitle="LENDING & INVESTMENT PORTFOLIOS"
      headerExtra={
        <Button variant="contained" size="small" onClick={() => setOpen(true)}
          startIcon={<Plus size={14} />}
          data-testid="add-borrower-btn"
          sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
          Onboard borrower
        </Button>
      }
    >
      {/* Filters */}
      <Paper sx={{ p: 1.4, mb: 2, display: "flex", gap: 1.2, alignItems: "center", flexWrap: "wrap" }} data-testid="borrowers-filters">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#6B7280", fontSize: 11.5, fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          <Filter size={13} /> Filters
        </Box>
        <TextField
          size="small" placeholder="Search by name or CIF…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search size={14} style={{ marginRight: 6, color: "#94A3B8" }} /> }}
          inputProps={{ "data-testid": "borrowers-search" }}
          sx={{ minWidth: 240 }}
        />
        <TextField size="small" select label="Portfolio" value={filterPortfolio}
          onChange={(e) => setFilterPortfolio(e.target.value)}
          SelectProps={{ inputProps: { "data-testid": "filter-portfolio" } }}
          sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="lending">Lending</MenuItem>
          <MenuItem value="investment">Investment</MenuItem>
        </TextField>
        <TextField size="small" select label="Industry" value={filterIndustry}
          onChange={(e) => setFilterIndustry(e.target.value)}
          SelectProps={{ inputProps: { "data-testid": "filter-industry" } }}
          sx={{ minWidth: 180 }}>
          <MenuItem value="">All sectors</MenuItem>
          {INDUSTRIES.map((i) => <MenuItem key={i} value={i}>{i.replace("_", " ")}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Chip label={`${filtered.length} ${filtered.length === 1 ? "borrower" : "borrowers"}`}
          size="small" data-testid="borrower-count-chip" />
      </Paper>

      <Paper sx={{ p: 0, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>
        ) : (
          <Table size="small" data-testid="borrowers-table">
            <TableHead>
              <TableRow>
                <TableCell>CIF / Name</TableCell>
                <TableCell>Type · Portfolio</TableCell>
                <TableCell>Industry</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell align="right">Tenor</TableCell>
                <TableCell>RM</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: "#94A3B8", fontSize: 13 }}>
                  No borrowers match the filter.
                </TableCell></TableRow>
              )}
              {filtered.map((b) => (
                <TableRow key={b.borrower_id} hover data-testid={`borrower-row-${b.cif_no}`}>
                  <TableCell>
                    <Box className="cb-mono" sx={{ fontSize: 11, color: "#6B7280", letterSpacing: "0.04em" }}>{b.cif_no}</Box>
                    <Box sx={{ fontWeight: 600, color: "#111827" }}>{b.name}</Box>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={b.type} sx={{ mr: 0.5, fontSize: 10, textTransform: "uppercase" }} />
                    <Chip size="small" label={b.portfolio}
                      sx={{
                        fontSize: 10, textTransform: "uppercase",
                        background: b.portfolio === "investment" ? "#E8F0E9" : "#F0EFEA",
                        color: b.portfolio === "investment" ? "#2E5A35" : "#4B5563",
                      }} />
                  </TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{b.industry?.replace("_", " ")}</TableCell>
                  <TableCell className="cb-mono">{b.credit_rating || "—"}</TableCell>
                  <TableCell align="right" className="cb-mono">{fmtINR(b.outstanding_inr)}</TableCell>
                  <TableCell align="right" className="cb-mono">{b.tenor_years || "—"}y</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{b.relationship_manager || "—"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View detail">
                      <IconButton size="small" onClick={() => navigate(`/borrowers/${b.borrower_id}`)} data-testid={`view-${b.cif_no}`}>
                        <ArrowRight size={15} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => remove(b)} data-testid={`del-${b.cif_no}`}>
                        <Trash2 size={14} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Onboard borrower dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: "Playfair Display, serif", fontWeight: 600 }}>Onboard new borrower</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.6, mt: 1 }}>
            <TextField size="small" required label="Borrower name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              inputProps={{ "data-testid": "bf-name" }} />
            <TextField size="small" select label="Type" value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              SelectProps={{ inputProps: { "data-testid": "bf-type" } }}>
              {["corporate", "sme", "msme", "retail"].map((t) => <MenuItem key={t} value={t}>{t.toUpperCase()}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Portfolio" value={form.portfolio}
              onChange={(e) => setForm({ ...form, portfolio: e.target.value })}
              SelectProps={{ inputProps: { "data-testid": "bf-portfolio" } }}>
              <MenuItem value="lending">Lending</MenuItem>
              <MenuItem value="investment">Investment</MenuItem>
            </TextField>
            <TextField size="small" select label="Industry" value={form.industry}
              onChange={(e) => setForm({ ...form, industry: e.target.value })}
              SelectProps={{ inputProps: { "data-testid": "bf-industry" } }}>
              {INDUSTRIES.map((i) => <MenuItem key={i} value={i}>{i.replace("_", " ")}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Credit rating" value={form.credit_rating}
              onChange={(e) => setForm({ ...form, credit_rating: e.target.value })}>
              {RATINGS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Facility type (e.g. Term Loan, Bond)" value={form.facility_type}
              onChange={(e) => setForm({ ...form, facility_type: e.target.value })} />
            <TextField size="small" label="Annual turnover (INR)" type="number" value={form.annual_turnover_inr}
              onChange={(e) => setForm({ ...form, annual_turnover_inr: e.target.value })} />
            <TextField size="small" label="Total sanctioned (INR)" type="number" value={form.total_exposure_inr}
              onChange={(e) => setForm({ ...form, total_exposure_inr: e.target.value })} />
            <TextField size="small" label="Outstanding (INR)" type="number" value={form.outstanding_inr}
              onChange={(e) => setForm({ ...form, outstanding_inr: e.target.value })}
              inputProps={{ "data-testid": "bf-outstanding" }} />
            <TextField size="small" label="Tenor (years)" type="number" value={form.tenor_years}
              onChange={(e) => setForm({ ...form, tenor_years: e.target.value })} />
            <TextField size="small" label="LTV (%)" type="number" value={form.ltv_pct}
              onChange={(e) => setForm({ ...form, ltv_pct: e.target.value })} />
            <TextField size="small" label="Relationship Manager" value={form.relationship_manager}
              onChange={(e) => setForm({ ...form, relationship_manager: e.target.value })} />
            <TextField size="small" label="Registered office PIN" value={form.registered_office_pin}
              onChange={(e) => setForm({ ...form, registered_office_pin: e.target.value })} />
            <TextField size="small" label="Registered office state" value={form.registered_office_state}
              onChange={(e) => setForm({ ...form, registered_office_state: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.name}
            data-testid="bf-save"
            sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
            {saving ? "Saving…" : "Onboard borrower"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
